import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  CheckCircle as VerifyIcon,
  PictureAsPdf as PdfIcon,
  Inventory as ReceiveIcon,
} from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../../../services/purchaseService';
import { PurchaseBill } from '../../../types/purchase';
import { formatCurrency } from '../../../utils/formatters';

export const PurchaseBillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [bill, setBill] = useState<PurchaseBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [receiveWarehouseId, setReceiveWarehouseId] = useState('');

  useEffect(() => {
    if (id) {
      fetchBill();
    }
  }, [id]);

  useEffect(() => {
    if (!receiveDialogOpen) return;
    const token = localStorage.getItem('token');
    fetch('/api/admin/inventory/warehouses', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : Promise.resolve([])))
      .then((data) => {
        setWarehouses(data);
        const savedWarehouseId = localStorage.getItem('admin_selectedWarehouseId');
        const defaultId = savedWarehouseId && data.some((w: any) => w.id === savedWarehouseId)
          ? savedWarehouseId
          : (data[0]?.id || '');
        setReceiveWarehouseId((prev) => prev || defaultId);
      })
      .catch(() => setWarehouses([]));
  }, [receiveDialogOpen]);

  const fetchBill = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data: any = await PurchaseService.getPurchaseBill(id);
      console.log('Fetched bill data:', data);
      setBill(data);
      // Handle both snake_case and camelCase
      const balance = data.balance_amount || data.balanceAmount || 0;
      setPaymentAmount(balance.toString());
    } catch (error) {
      console.error('Failed to fetch bill:', error);
      enqueueSnackbar('Failed to load bill details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!bill) return;
    try {
      setActionLoading(true);
      await PurchaseService.verifyPurchaseBill(bill.id);
      enqueueSnackbar('Bill verified successfully', { variant: 'success' });
      fetchBill();
    } catch (error) {
      enqueueSnackbar('Failed to verify bill', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceiveGoods = async () => {
    if (!bill || !receiveWarehouseId) return;
    try {
      setActionLoading(true);
      const result = await PurchaseService.receiveGoodsForBill(bill.id, receiveWarehouseId);
      enqueueSnackbar(result.message || 'Goods received successfully', { variant: 'success' });
      if (result.warnings?.length) {
        result.warnings.forEach((w) => enqueueSnackbar(w, { variant: 'warning' }));
      }
      setReceiveDialogOpen(false);
      fetchBill();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to receive goods', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!bill) return;
    try {
      setActionLoading(true);
      await PurchaseService.recordPayment(
        bill.id,
        parseFloat(paymentAmount),
        paymentDate,
        paymentMethod,
        paymentReference,
        paymentNotes
      );
      enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
      setPaymentDialogOpen(false);
      fetchBill();
    } catch (error) {
      enqueueSnackbar('Failed to record payment', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!bill) return;
    try {
      const blob = await PurchaseService.generatePurchaseBillPDF(bill.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bill.billNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      draft: 'default',
      verified: 'primary',
      partially_paid: 'warning',
      paid: 'success',
      overdue: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!bill) {
    return (
      <Box>
        <Typography>Bill not found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/purchase/bills')}>
            Back
          </Button>
          <Typography variant="h4">Bill {(bill as any).bill_number || bill.billNumber}</Typography>
          <Chip
            label={((bill as any).status || bill.status || 'draft').toUpperCase().replace('_', ' ')}
            color={getStatusColor((bill as any).status || bill.status || 'draft')}
          />
        </Box>
        <Box display="flex" gap={1}>
          {((bill as any).status || bill.status) === 'draft' && (
            <Button
              variant="outlined"
              startIcon={<ReceiveIcon />}
              onClick={() => setReceiveDialogOpen(true)}
              disabled={actionLoading}
            >
              Receive Goods
            </Button>
          )}
          {['draft', 'received'].includes((bill as any).status || bill.status) && (
            <Button
              variant="outlined"
              startIcon={<VerifyIcon />}
              onClick={handleVerify}
              disabled={actionLoading}
            >
              Verify
            </Button>
          )}
          {((bill as any).balance_amount || bill.balanceAmount || 0) > 0 &&
            ((bill as any).status || bill.status) !== 'draft' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={() => setPaymentDialogOpen(true)}
                disabled={actionLoading}
              >
                Record Payment
              </Button>
            )}
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>
          {((bill as any).status || bill.status) === 'draft' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/purchase/bills/${bill.id}/edit`)}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Bill Number
                </Typography>
                <Typography variant="body1">{(bill as any).bill_number || bill.billNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  PO Number
                </Typography>
                <Typography variant="body1">{(bill as any).po_number || bill.purchaseOrder?.poNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Bill Date
                </Typography>
                <Typography variant="body1">
                  {new Date((bill as any).bill_date || bill.billDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography variant="body1">
                  {(bill as any).due_date || bill.dueDate
                    ? new Date((bill as any).due_date || bill.dueDate).toLocaleDateString()
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Supplier Information
            </Typography>
            <Typography variant="body1">
              {(bill as any).supplier_name || bill.supplier?.businessName || '-'}
            </Typography>
            {(bill as any).supplier_gstin && (
              <Typography variant="body2" color="text.secondary">
                GSTIN: {(bill as any).supplier_gstin}
              </Typography>
            )}
            {bill.supplier?.email && (
              <Typography variant="body2" color="text.secondary">
                {bill.supplier.email}
              </Typography>
            )}
            {bill.supplier?.phone && (
              <Typography variant="body2" color="text.secondary">
                {bill.supplier.phone}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bill as any).items?.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.variant?.product?.name || item.product_name || item.description || '-'}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.unit_price || item.unitPrice || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.tax_amount || item.taxAmount || item.total_tax_amount || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.line_total || item.lineTotal || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!(bill as any).items || (bill as any).items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No items found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2">Subtotal:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" align="right">
                  {formatCurrency((bill as any).subtotal || bill.subtotal || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Tax:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" align="right">
                  {formatCurrency((bill as any).tax_amount || bill.taxAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">Total:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  {formatCurrency((bill as any).total_amount || bill.totalAmount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="error">
                  Balance:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" align="right" color="error">
                  {formatCurrency((bill as any).balance_amount || bill.balanceAmount || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </TextField>
            <TextField
              fullWidth
              label="Reference Number"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Transaction ID, Cheque No, etc."
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Goods Dialog */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Goods</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Choose the warehouse these goods actually arrived at — stock will be increased there.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Warehouse</InputLabel>
              <Select
                label="Warehouse"
                value={receiveWarehouseId}
                onChange={(e) => setReceiveWarehouseId(e.target.value)}
              >
                {warehouses.map((wh) => (
                  <MenuItem key={wh.id} value={wh.id}>{wh.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReceiveGoods}
            variant="contained"
            disabled={actionLoading || !receiveWarehouseId}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Receive Goods'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
